/**
 * Página dedicada de criação/edição de agente IA.
 *
 * Substitui o modal grande que o usuário criticou no code review da UX —
 * uma página inteira permite layout em 2 colunas (config à esquerda, prompt
 * grande à direita), tools clicáveis como chips, e fluxo "criar a partir
 * de template" sem perder contexto.
 *
 * URL:
 *   /agents/new                 — agente em branco
 *   /agents/new?template=vendas — clone de template
 *   /agents/:id                 — edição (cria nova versão imutável no save)
 */

import { useEffect, useMemo, useState } from "react";
import { useLocation, useRoute, useParams } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Bot, ArrowLeft, Save, History, Check, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AGENT_TEMPLATES, getAgentTemplate, type AgentTemplate } from "./templates";

// ── Catálogo de tools (espelha server/routing-engine/tools/index.ts) ──────────
//
// Mantido aqui pra evitar trip ao backend só pra listar; quando adicionarmos
// novas tools, atualizar AMBOS os lados (acoplamento aceitável — registry
// muda raramente). Em F1.4+ podemos expor via tRPC.
type ToolMeta = {
  name: string;
  label: string;
  description: string;
  category: "read" | "mutation" | "control";
  risky?: boolean;
};

const AVAILABLE_TOOLS: ToolMeta[] = [
  // Reads
  {
    name: "queryKnowledgeBase",
    label: "Consultar Base de Conhecimento",
    description:
      "RAG sobre os documentos do agente. Sempre permitir para agentes que respondem fatos sobre produto/processo.",
    category: "read",
  },
  {
    name: "getContactProfile",
    label: "Ler perfil do contato",
    description:
      "Nome, empresa, cidade, tags. Útil para personalizar abertura. Sem args.",
    category: "read",
  },
  // Mutations seguras
  {
    name: "replyDirectly",
    label: "Responder ao cliente",
    description:
      "Envia mensagem de texto via WhatsApp. Praticamente todo agente precisa disso.",
    category: "mutation",
  },
  {
    name: "addTag",
    label: "Adicionar tag",
    description:
      "Marca estado do contato (ex: 'qualificado'). Tag dispara automações configuradas.",
    category: "mutation",
  },
  {
    name: "removeTag",
    label: "Remover tag",
    description: "Desfaz estado anterior. Cuidado: pode disparar automações de tag_removed.",
    category: "mutation",
  },
  {
    name: "handoffToHuman",
    label: "Escalar para humano",
    description:
      "Encerra o atendimento por IA, atribui agente humano e ativa cooldown de 4h.",
    category: "control",
  },
  // Mutations fortes
  {
    name: "updateContact",
    label: "Atualizar campos do contato",
    description:
      "name, companyName, city, state, sector, notes. NÃO altera telefone/email.",
    category: "mutation",
    risky: true,
  },
  {
    name: "routeToFlow",
    label: "Disparar fluxo existente",
    description:
      "Roda em background um fluxo do Flow Builder. Use quando há um caminho determinístico melhor.",
    category: "control",
    risky: true,
  },
  {
    name: "enrollInSequence",
    label: "Inscrever em sequência (drip)",
    description: "Dedup automático — não inscreve se já houver enrollment ativo.",
    category: "mutation",
    risky: true,
  },
  {
    name: "switchAgent",
    label: "Transferir para outro agente",
    description:
      "Multi-agent dispatch. Detecção de ciclo embutida (rejeita A→B→A).",
    category: "control",
    risky: true,
  },
];

const CATEGORY_LABELS: Record<string, string> = {
  read: "Leitura (idempotente)",
  mutation: "Mutação",
  control: "Controle de fluxo",
};

const MODEL_SUGGESTIONS: Array<{ label: string; group: string; value: string }> = [
  { group: "OpenAI", label: "GPT-4o mini (rápido + barato)", value: "gpt-4o-mini" },
  { group: "OpenAI", label: "GPT-4o", value: "gpt-4o" },
  { group: "Anthropic", label: "Claude Haiku 4.5 (rápido + barato)", value: "claude-haiku-4-5" },
  { group: "Anthropic", label: "Claude Sonnet 4.6", value: "claude-sonnet-4-6" },
  { group: "Anthropic", label: "Claude Opus 4.7", value: "claude-opus-4-7" },
  { group: "Google", label: "Gemini 2.5 Flash (rápido + barato)", value: "gemini-2.5-flash" },
  { group: "Google", label: "Gemini 2.5 Pro", value: "gemini-2.5-pro" },
];

type FormState = {
  key: string;
  name: string;
  role: "bot" | "specialist" | "router";
  credentialId: number | null;
  model: string;
  temperature: number;
  systemPrompt: string;
  allowedTools: Set<string>;
};

const EMPTY_FORM: FormState = {
  key: "",
  name: "",
  role: "specialist",
  credentialId: null,
  model: "gpt-4o-mini",
  temperature: 0.3,
  systemPrompt: "",
  allowedTools: new Set([
    "replyDirectly",
    "queryKnowledgeBase",
    "addTag",
    "handoffToHuman",
  ]),
};

function fromTemplate(t: AgentTemplate): FormState {
  return {
    key: t.key,
    name: t.name,
    role: t.role,
    credentialId: null,
    model: t.suggestedModel,
    temperature: t.temperature,
    systemPrompt: t.systemPrompt,
    allowedTools: new Set(t.allowedTools),
  };
}

export default function AgentEditor() {
  const params = useParams<{ id?: string }>();
  const [, navigate] = useLocation();
  const [matchNew] = useRoute("/agents/new");
  const isNew = matchNew || !params.id || params.id === "new";
  const agentId = !isNew && params.id ? Number(params.id) : null;

  // Template via query string ?template=vendas
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const templateKey = searchParams.get("template");
  const template = templateKey ? getAgentTemplate(templateKey) : undefined;

  const [form, setForm] = useState<FormState>(
    template ? fromTemplate(template) : EMPTY_FORM,
  );

  // M-1/NU-3 — dirty flag pra avisar antes de descartar edições. False após
  // hidratação (load do server ou template); true após qualquer setForm
  // do usuário.
  const [isDirty, setIsDirty] = useState(false);

  const utils = trpc.useUtils();
  const { data: credentials = [] } = trpc.llmCredentials.list.useQuery();
  const { data: existingAgent } = trpc.agents.getById.useQuery(
    { id: agentId ?? 0 },
    { enabled: !!agentId },
  );
  const { data: versions = [] } = trpc.agents.listVersions.useQuery(
    { agentId: agentId ?? 0 },
    { enabled: !!agentId },
  );

  // Hidrata form quando agente existente carrega
  useEffect(() => {
    if (!existingAgent || !existingAgent.currentVersion) return;
    setForm({
      key: existingAgent.key,
      name: existingAgent.name,
      role: existingAgent.role as FormState["role"],
      credentialId: existingAgent.credentialId ?? null,
      model: existingAgent.model,
      temperature: Number(existingAgent.temperature),
      systemPrompt: existingAgent.currentVersion.systemPrompt,
      allowedTools: new Set(existingAgent.allowedTools ?? []),
    });
    setIsDirty(false); // hidratação inicial não conta como dirty
  }, [existingAgent]);

  // Wrapper que marca dirty automaticamente. Substitui o setForm direto em
  // todos os event handlers do usuário.
  function setFormDirty(updater: FormState | ((f: FormState) => FormState)) {
    setForm(updater as any);
    setIsDirty(true);
  }

  // M-1 — Avisa antes de fechar a aba/recarregar com edições pendentes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  function confirmDiscardIfDirty(message: string): boolean {
    if (!isDirty) return true;
    return window.confirm(message);
  }

  const createMutation = trpc.agents.create.useMutation({
    onSuccess: (a) => {
      utils.agents.list.invalidate();
      setIsDirty(false);
      toast.success("Agente criado (v1)");
      if (a?.id) navigate(`/agents/${a.id}`);
      else navigate("/agents");
    },
    onError: (err) => toast.error(`Falha: ${err.message}`),
  });

  const updateMutation = trpc.agents.update.useMutation({
    onSuccess: () => {
      utils.agents.list.invalidate();
      utils.agents.getById.invalidate({ id: agentId ?? 0 });
      utils.agents.listVersions.invalidate({ agentId: agentId ?? 0 });
      setIsDirty(false); // useEffect re-hidratará form com nova versão
      toast.success("Agente atualizado — nova versão criada");
    },
    onError: (err) => toast.error(`Falha: ${err.message}`),
  });

  const rollbackMutation = trpc.agents.rollback.useMutation({
    onSuccess: () => {
      utils.agents.list.invalidate();
      utils.agents.getById.invalidate({ id: agentId ?? 0 });
      utils.agents.listVersions.invalidate({ agentId: agentId ?? 0 });
      setIsDirty(false); // hidratação pós-rollback é fonte de verdade
      toast.success("Rollback aplicado");
    },
  });

  const deleteMutation = trpc.agents.delete.useMutation({
    onSuccess: () => {
      utils.agents.list.invalidate();
      toast.success("Agente removido");
      navigate("/agents");
    },
  });

  function toggleTool(name: string) {
    setFormDirty((f) => {
      const next = new Set(f.allowedTools);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return { ...f, allowedTools: next };
    });
  }

  function submit() {
    const payload = {
      name: form.name,
      role: form.role,
      credentialId: form.credentialId,
      model: form.model,
      temperature: form.temperature,
      systemPrompt: form.systemPrompt,
      allowedTools: Array.from(form.allowedTools),
    };

    if (isNew) {
      createMutation.mutate({ key: form.key, ...payload });
    } else if (agentId !== null) {
      updateMutation.mutate({ id: agentId, ...payload });
    }
  }

  const submitting = createMutation.isPending || updateMutation.isPending;
  const canSubmit =
    !!form.key.trim() && !!form.name.trim() && !!form.systemPrompt.trim();

  const credentialMissing = !form.credentialId && AVAILABLE_TOOLS.length > 0;

  return (
    <PermissionGuard permission="ai:agents_manage">
      <DashboardLayout>
        <div className="flex flex-col gap-6 p-6 max-w-6xl mx-auto w-full">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (
                    confirmDiscardIfDirty(
                      "Você tem edições não salvas. Descartar e sair?",
                    )
                  ) {
                    navigate("/agents");
                  }
                }}
                className="gap-1.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Agentes
              </Button>
              <div className="border-l border-border h-6" />
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="w-6 h-6 text-violet-600 shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-xl font-bold truncate">
                    {isNew ? "Novo agente" : existingAgent?.name ?? "Editar agente"}
                  </h1>
                  {!isNew && existingAgent?.currentVersion && (
                    <p className="text-xs text-muted-foreground">
                      versão atual: v{existingAgent.currentVersion.version} ·{" "}
                      {existingAgent.key}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {!isNew && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (
                      confirm(
                        "Excluir agente? Todas as versões serão removidas. Conversas em curso farão fallback para humano.",
                      )
                    ) {
                      deleteMutation.mutate({ id: agentId! });
                    }
                  }}
                  className="text-destructive hover:text-destructive gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Excluir
                </Button>
              )}
              <Button
                onClick={submit}
                disabled={!canSubmit || submitting}
                className="gap-1.5"
              >
                <Save className="w-4 h-4" />
                {submitting
                  ? "Salvando…"
                  : isNew
                    ? "Criar agente"
                    : "Salvar nova versão"}
              </Button>
            </div>
          </div>

          {/* Alerts */}
          {credentialMissing && (
            <Card className="border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/20">
              <CardContent className="pt-3 pb-3 flex items-start gap-2 text-sm">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <strong>Agente sem credencial.</strong> Sem associar uma
                  chave BYOK, o agente ficará marcado como inativo após salvar.
                  Cadastre uma chave em{" "}
                  <a href="/settings?tab=integrations&integration=llm" className="underline">
                    Chaves de API
                  </a>{" "}
                  e selecione abaixo.
                </div>
              </CardContent>
            </Card>
          )}

          {/* Grid principal: meta à esquerda + prompt à direita */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
            {/* Coluna esquerda — Meta */}
            <div className="flex flex-col gap-4">
              <Card>
                <CardContent className="pt-4 pb-4 flex flex-col gap-4">
                  <h2 className="text-sm font-semibold">Identidade</h2>
                  <div className="flex flex-col gap-1.5">
                    <Label>Identificador (key)</Label>
                    <Input
                      placeholder="vendas, suporte_l1, onboarding…"
                      value={form.key}
                      disabled={!isNew}
                      onChange={(e) =>
                        setFormDirty((f) => ({
                          ...f,
                          key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Slug imutável usado em referências de fluxo.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Nome de exibição</Label>
                    <Input
                      placeholder="Ex: Agente de Vendas"
                      value={form.name}
                      onChange={(e) =>
                        setFormDirty((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Papel</Label>
                    <Select
                      value={form.role}
                      onValueChange={(v) =>
                        setFormDirty((f) => ({ ...f, role: v as FormState["role"] }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bot">Bot (genérico)</SelectItem>
                        <SelectItem value="specialist">Especialista</SelectItem>
                        <SelectItem value="router">Roteador (decide quem atende)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 pb-4 flex flex-col gap-4">
                  <h2 className="text-sm font-semibold">LLM & Custo</h2>
                  <div className="flex flex-col gap-1.5">
                    <Label>Credencial (BYOK)</Label>
                    <Select
                      value={form.credentialId?.toString() ?? "none"}
                      onValueChange={(v) =>
                        setFormDirty((f) => ({
                          ...f,
                          credentialId: v === "none" ? null : Number(v),
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Nenhuma" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhuma (agente inativo)</SelectItem>
                        {credentials.map((c) => (
                          <SelectItem key={c.id} value={c.id.toString()}>
                            {c.label}{" "}
                            <span className="text-muted-foreground">
                              ({c.provider})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Modelo</Label>
                    <Select
                      value={form.model}
                      onValueChange={(v) => setFormDirty((f) => ({ ...f, model: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MODEL_SUGGESTIONS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            <span className="font-mono text-xs mr-2">{m.group}</span>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Modelos baratos cabem em decisões simples; reserve modelos
                      premium para agentes que negociam ou raciocinam complexo.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>Temperature: {form.temperature.toFixed(1)}</Label>
                    <input
                      type="range"
                      min={0}
                      max={1.5}
                      step={0.1}
                      value={form.temperature}
                      onChange={(e) =>
                        setFormDirty((f) => ({
                          ...f,
                          temperature: Number(e.target.value),
                        }))
                      }
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      0 = previsível; 1+ = criativo. Vendas/Suporte usam 0.2-0.4.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {!isNew && versions.length > 0 && (
                <Card>
                  <CardContent className="pt-4 pb-4 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <History className="w-4 h-4 text-muted-foreground" />
                      <h2 className="text-sm font-semibold">Versões</h2>
                    </div>
                    {versions.slice(0, 5).map((v) => {
                      const isActive =
                        existingAgent?.currentVersionId === v.id;
                      return (
                        <div
                          key={v.id}
                          className="flex items-center justify-between text-xs gap-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge variant="outline" className="shrink-0">
                              v{v.version}
                            </Badge>
                            <span className="text-muted-foreground truncate">
                              {new Date(v.createdAt).toLocaleString("pt-BR")}
                            </span>
                          </div>
                          {isActive ? (
                            <span className="text-green-600 font-medium">atual</span>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => {
                                // NU-3 — rollback re-hidrata form, descartando edições.
                                // Confirmar se está dirty.
                                if (
                                  confirmDiscardIfDirty(
                                    `Voltar para a versão v${v.version} vai descartar suas edições não salvas. Continuar?`,
                                  )
                                ) {
                                  rollbackMutation.mutate({
                                    agentId: agentId!,
                                    versionId: v.id,
                                  });
                                }
                              }}
                            >
                              Usar
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    {versions.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        +{versions.length - 5} versões antigas
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Coluna direita — Prompt + Tools */}
            <div className="flex flex-col gap-4">
              <Card>
                <CardContent className="pt-4 pb-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold">Prompt do sistema</h2>
                    <span className="text-xs text-muted-foreground">
                      {form.systemPrompt.length} chars
                    </span>
                  </div>
                  <Textarea
                    placeholder="Você é um agente de…"
                    rows={20}
                    value={form.systemPrompt}
                    onChange={(e) =>
                      setFormDirty((f) => ({ ...f, systemPrompt: e.target.value }))
                    }
                    className="font-mono text-sm leading-relaxed resize-y min-h-[400px]"
                  />
                  <p className="text-xs text-muted-foreground">
                    Cada edição cria uma nova versão imutável. Você pode
                    rollback no painel à esquerda sem perder histórico.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-4 pb-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold">
                      Ferramentas permitidas
                    </h2>
                    <span className="text-xs text-muted-foreground">
                      {form.allowedTools.size} de {AVAILABLE_TOOLS.length}{" "}
                      selecionadas
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Clique para ativar/desativar. Apenas as marcadas ficam
                    disponíveis para o LLM chamar.
                  </p>
                  {(["read", "mutation", "control"] as const).map((cat) => {
                    const tools = AVAILABLE_TOOLS.filter((t) => t.category === cat);
                    if (tools.length === 0) return null;
                    return (
                      <div key={cat} className="flex flex-col gap-2">
                        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {CATEGORY_LABELS[cat]}
                        </h3>
                        <div className="flex flex-col gap-1.5">
                          {tools.map((t) => {
                            const active = form.allowedTools.has(t.name);
                            return (
                              <button
                                key={t.name}
                                type="button"
                                onClick={() => toggleTool(t.name)}
                                className={`text-left rounded-md border p-3 transition-colors ${
                                  active
                                    ? "border-violet-400 bg-violet-50 dark:border-violet-700 dark:bg-violet-950/30"
                                    : "border-border hover:border-border/80 hover:bg-muted/30"
                                }`}
                              >
                                <div className="flex items-start gap-2">
                                  <div
                                    className={`mt-0.5 w-4 h-4 rounded border shrink-0 flex items-center justify-center ${
                                      active
                                        ? "bg-violet-600 border-violet-600"
                                        : "border-muted-foreground/40"
                                    }`}
                                  >
                                    {active && (
                                      <Check className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-medium">
                                        {t.label}
                                      </span>
                                      <code className="text-xs font-mono text-muted-foreground">
                                        {t.name}
                                      </code>
                                      {t.risky && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs text-amber-600 border-amber-300"
                                        >
                                          ação forte
                                        </Badge>
                                      )}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      {t.description}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Templates atalho (só na criação em branco) */}
          {isNew && !template && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <p className="text-xs text-muted-foreground mb-3">
                  Começar a partir de um template:
                </p>
                <div className="flex flex-wrap gap-2">
                  {AGENT_TEMPLATES.map((t) => (
                    <Button
                      key={t.key}
                      variant="outline"
                      size="sm"
                      onClick={() => setFormDirty(fromTemplate(t))}
                    >
                      {t.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DashboardLayout>
    </PermissionGuard>
  );
}
